import { take } from 'ramda';
import { colors } from 'styles/provider';
import { changeIndexes, fitStringToWidth, textTooLongToFit, wrapText } from './utils';

const flagColors = (index, cog = false) => {
  const hex80Percentage = 'CC';
  const hex75Percentage = 'BF';

  if (!cog) {
    // Fatigue colors
    if (index === 0 || index === 3) {
      return {
        text: colors.clrPurple,
        base: colors.clrPurple + hex80Percentage,
        pole: colors.clrPurple,
        flag: colors.clrPurpleLight + hex75Percentage,
      };
    } else {
      return {
        text: colors.clrSecondary,
        base: colors.clrSecondary + hex80Percentage,
        pole: colors.clrSecondary,
        flag: colors.clrSecondaryLight + hex75Percentage,
      };
    }
  } else {
    // Cognition colors
    if (index === 0 || index === 3) {
      return {
        text: colors.clrBrandBlueDefault,
        base: colors.clrBrandBlueDefault + hex80Percentage,
        pole: colors.clrBrandBlueDefault,
        flag: colors.clrBrandBlueLight + hex75Percentage,
      };
    } else {
      return {
        text: colors.clrWarningDefault,
        base: colors.clrWarningDefault + hex80Percentage,
        pole: colors.clrWarningDefault,
        flag: colors.clrWarningLight + hex75Percentage,
      };
    }
  }
};

const scale = 2;

const dimensions = {
  flagBaseWidth: 18 * scale,
  flagBaseHeight: 14 * scale,
  poleWidth: 2 * scale,
  poleHeight: 50 * scale,
  flagWidth: 250 * scale,
  flagWidthMax: 430 * scale,
  flagHeight: 25 * scale,

  textPadding: 10 * scale,

  lineHeight: 20 * scale,

  flagFontSize: 1.3 * scale,
  flagFontSizeSmall: 1.1 * scale,
  targetFontSize: 2.2 * scale,

  fatigueCoords: [
    [290 * scale, 245 * scale],
    [105 * scale, 360 * scale],
    [225 * scale, 465 * scale],
    /* First to drop if less than 4 */ [118 * scale, 430 * scale],
  ],
  cogCoords: [
    [350 * scale, 260 * scale],
    [670 * scale, 325 * scale],
    [678 * scale, 500 * scale],
    [605 * scale, 395 * scale],
  ],
};

const drawPole = (ctx, cog, coords, index, twoLines = false) => {
  const poleHeight = twoLines ? dimensions.poleHeight + dimensions.lineHeight : dimensions.poleHeight;
  const poleY = coords[index][1] - poleHeight;

  const pole = new Path2D();
  pole.rect(coords[index][0] - 1, poleY, dimensions.poleWidth, poleHeight);
  ctx.fillStyle = flagColors(index, cog).pole;
  ctx.fill(pole);
};

const drawFlag = (ctx, cog, coords, index, negative, text, twoLines = false) => {
  const potentialFlagWidth = ctx.measureText(text).width + dimensions.textPadding * 2;
  const flagWidth = !twoLines
    ? potentialFlagWidth < dimensions.flagWidthMax
      ? potentialFlagWidth
      : dimensions.flagWidthMax
    : dimensions.flagWidth;

  const flagHeight = twoLines ? dimensions.flagHeight + dimensions.lineHeight : dimensions.flagHeight;

  const flagX = coords[index][0] - (negative ? 1 : -dimensions.poleWidth);
  const flagY = coords[index][1] - (!twoLines ? dimensions.poleHeight : dimensions.poleHeight + dimensions.lineHeight);
  const flagWidthActual = negative ? -flagWidth : flagWidth;

  const flag = new Path2D();
  flag.rect(flagX, flagY, flagWidthActual, flagHeight);
  ctx.fillStyle = flagColors(index, cog).flag;
  ctx.fill(flag);
};

const drawText = (ctx, cog, coords, index, negative, text, twoLines = false) => {
  ctx.fillStyle = flagColors(index, cog).text;

  const potentialFlagWidth = ctx.measureText(text).width + dimensions.textPadding * 2;
  const flagWidth = !twoLines
    ? potentialFlagWidth < dimensions.flagWidthMax
      ? potentialFlagWidth
      : dimensions.flagWidthMax
    : dimensions.flagWidth;

  const textCoords = [negative ? -(flagWidth - 10 * scale) : 10 * scale, twoLines ? -52 * scale : -32 * scale];
  if (twoLines) {
    let wrappedText = wrapText(
      ctx,
      text,
      coords[index][0] + textCoords[0],
      coords[index][1] + textCoords[1],
      230 * scale,
      dimensions.lineHeight,
    );
    if (wrappedText.length > 2) {
      ctx.font = `bold ${dimensions.flagFontSizeSmall}rem titillium web`;
      wrappedText = wrapText(
        ctx,
        text,
        coords[index][0] + textCoords[0],
        coords[index][1] + textCoords[1],
        230 * scale,
        dimensions.lineHeight,
      );
    }
    take(2, wrappedText).forEach((item, i) => {
      ctx.fillText(item[0] + `${i === 1 && wrappedText.length > 2 ? '...' : ''}`, item[1], item[2]);
    });
  } else {
    // 0.955 to account for ellipsis and other paddings when shortening text
    const textWontFit = textTooLongToFit(text, ctx, flagWidth * 0.955);
    if (textWontFit) {
      ctx.font = `bold ${dimensions.flagFontSizeSmall}rem titillium web`;
    }
    ctx.fillText(
      fitStringToWidth(ctx, text, flagWidth - dimensions.textPadding * 2),
      coords[index][0] + textCoords[0],
      coords[index][1] + textCoords[1],
    );

    // Normalize font size
    ctx.font = `bold ${dimensions.flagFontSize}rem titillium web`;
  }
};

const drawFlagsWithTexts = (ctx, fatigues, cognitions, fatigueGoal, cognitionGoal) => {
  // Adjust arrays first to match what is wanted
  const prunedFatigues = Array.isArray(fatigues) ? changeIndexes(fatigues.filter(f => f)) : [];
  const prunedCognitions = Array.isArray(cognitions) ? changeIndexes(cognitions.filter(f => f)) : [];

  const drawAll = (index, cog = false, text) => {
    const coords = cog ? dimensions.cogCoords : dimensions.fatigueCoords;

    // Draw flag base
    const circle = new Path2D();
    circle.ellipse(
      coords[index][0],
      coords[index][1],
      dimensions.flagBaseWidth,
      dimensions.flagBaseHeight,
      0,
      2 * Math.PI,
      false,
    );
    ctx.fillStyle = flagColors(index, cog).base;
    ctx.fill(circle);

    ctx.font = `bold ${dimensions.flagFontSize}rem titillium web`;

    const textShouldHaveTwoLines = index === 0 && textTooLongToFit(text, ctx, 240 * scale);
    const longerFlag = index > 0 && textTooLongToFit(text, ctx, 240 * scale);

    // Draw pole
    drawPole(ctx, cog, coords, index, textShouldHaveTwoLines);

    // Flag should be drawn in the opposite direction
    const negative = (cog && index > 0) || (!cog && index === 0);

    // Draw flag
    drawFlag(ctx, cog, coords, index, negative, text, textShouldHaveTwoLines, longerFlag);

    // Draw text
    drawText(ctx, cog, coords, index, negative, text, textShouldHaveTwoLines, longerFlag);
  };

  take(4, prunedFatigues).forEach((text, i) => {
    drawAll(i, false, text);
  });

  take(4, prunedCognitions).forEach((text, i) => {
    drawAll(i, true, text);
  });

  // Draw fatigue target text
  ctx.font = `bold ${dimensions.targetFontSize}rem titillium web`;
  ctx.fillStyle = colors.clrPrimary;
  const wrappedText = wrapText(ctx, fatigueGoal || '', 70 * scale, 70 * scale, 230 * scale, 25 * scale);
  take(4, wrappedText).forEach(item => {
    ctx.fillText(item[0], item[1], item[2]);
  });

  // Draw cognition target text
  ctx.font = `bold ${dimensions.targetFontSize}rem titillium web`;
  ctx.fillStyle = colors.clrPrimary;
  const wrappedTextCog = wrapText(ctx, cognitionGoal || '', 437 * scale, 70 * scale, 230 * scale, 25 * scale);
  take(4, wrappedTextCog).forEach(item => {
    ctx.fillText(item[0], item[1], item[2]);
  });
};

export default drawFlagsWithTexts;
